import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import MultiStepForm from './MultiStepForm';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface FormLightboxProps {
    isOpen: boolean;
    onClose: () => void;
    formId: string;
    formTitle: string;
    formDescription?: string;
    onSuccess?: (submissionId: string, message: string) => void;
    metadata?: { [key: string]: string };
    footnote?: string;
}

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const FormLightbox: React.FC<FormLightboxProps> = ({
    isOpen,
    onClose,
    formId,
    formTitle,
    formDescription,
    onSuccess,
    metadata,
    footnote
}) => {
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [countdown, setCountdown] = useState(5);
    const [formError, setFormError] = useState('');

    const { executeRecaptcha } = useRecaptcha({
        siteKey: RECAPTCHA_SITE_KEY,
        enabled: true
    });

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isOpen]);

    const handleGetRecaptchaToken = async (): Promise<string | undefined> => {
        const token = await executeRecaptcha(`submit_${formId}`);
        return token || undefined;
    };

    const handleSuccess = (submissionId: string, message: string) => {
        if (onSuccess) {
            onSuccess(submissionId, message);
        }
        setFormError('');
        setSuccessMessage(message);
        setShowSuccess(true);
        setCountdown(5);
    };

    useEffect(() => {
        if (!isOpen) return;
        setShowSuccess(false);
        setSuccessMessage('');
        setCountdown(5);
        setFormError('');
    }, [isOpen]);

    // Countdown timer for auto-close
    useEffect(() => {
        if (!showSuccess) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showSuccess, onClose]);

    const handleError = (error: string) => {
        console.error('Form submission error:', error);
        setFormError(error || 'Something went wrong while submitting the form.');
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Only close if clicking the backdrop itself, not child elements
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-stretch sm:items-center justify-center p-0 sm:p-4"
            onClick={handleBackdropClick}
        >
            <div
                className="bg-white shadow-xl border border-gray-200 w-full h-[100dvh] max-h-[100dvh] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:pt-0 sm:pb-0 flex flex-col overflow-hidden rounded-none sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative p-4 sm:p-6 border-b border-gray-200 flex items-start justify-between">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute left-4 top-3 h-16 w-24 opacity-40 bg-[radial-gradient(circle,_rgba(0,0,0,0.12)_2px,_transparent_2px)] [background-size:16px_16px]" />
                    </div>
                    <div className="relative z-10 pr-6">
                        <h2 className="text-xl sm:text-2xl font-headline font-semibold text-black mb-2">
                            {formTitle}
                        </h2>
                        {formDescription && (
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                {formDescription}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="relative z-10 text-gray-500 hover:text-black transition-colors p-2 -mt-1 -mr-1 rounded-lg hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content - Either Form or Success */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-white" data-form-scroll-container="true">
                    {showSuccess ? (
                        // Success Screen
                        <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 sm:px-6 text-center rounded-2xl border border-green-200 bg-green-50">
                            {/* Success Icon */}
                            <CheckCircle className="w-14 h-14 sm:w-16 sm:h-16 text-green-600 mb-5" strokeWidth={1.75} />

                            <h3 className="text-xl sm:text-2xl font-headline text-green-900 mb-3">
                                Message Received
                            </h3>

                            <p className="text-green-800 text-base sm:text-lg mb-8 max-w-md">
                                {successMessage || 'Your submission has been received successfully. We\'ll be in touch soon!'}
                            </p>

                            <button
                                onClick={onClose}
                                className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors duration-200"
                            >
                                Close ({countdown}s)
                            </button>
                        </div>
                    ) : (
                        // Form
                        <>
                            {formError && (
                                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                                    <p className="text-red-700 text-sm sm:text-base">{formError}</p>
                                </div>
                            )}
                            <MultiStepForm
                                formId={formId}
                                appearance="tracking"
                                onSuccess={handleSuccess}
                                onError={handleError}
                                getRecaptchaToken={handleGetRecaptchaToken}
                                metadata={metadata}
                            />
                        </>
                    )}
                </div>

                {/* Footer */}
                {!showSuccess && (
                    <div className="border-t border-gray-200">
                        {footnote && (
                            <div className="px-4 sm:px-6 py-3 text-xs text-gray-600 text-center bg-gray-50">
                                *{footnote}
                            </div>
                        )}
                        <div className="px-4 py-3 text-xs text-gray-500 text-center">
                            This site is protected by reCAPTCHA.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default FormLightbox;
